<template>
  <textarea
    :id="props.id"
    ref="textareaRef"
    :class="rootClasses"
    data-cy="ds-textarea"
    :name="props.name"
    :placeholder="props.placeholder"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :rows="props.rows"
    :required="props.required"
    :aria-invalid="props.hasError ? 'true' : undefined"
    :autocomplete="props.autocomplete"
    :maxlength="props.maxlength"
    :minlength="props.minlength"
    :value="normalizedModelValue"
    @focus="onTextareaFocus"
    @blur="onTextareaBlur"
  />
</template>

<script setup lang="ts">
import { computed, markRaw, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import type { DSTextareaEmits } from "../types/emits";
import type { EasyEditorApi, EasyEditorInstance } from "@/src/ckeditor/easy_editor/types/easyEditor";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { DS_TEXTAREA_CLASSES } from "@/src/design_system/form_elements/textarea/constants/classes";
import type { DSRichEditorConfig, DSTextareaModelValue } from "@/src/design_system/form_elements/textarea/types/props";
import { DS_STATE_CLASSES } from "@/src/design_system/shared/constants/stateClasses";

const EDITOR_READ_ONLY_LOCK = "dstextarea-rich-editor" as const;

interface DSTextareaRichEditorProps {
  id: string;
  name: string;
  placeholder?: string;
  rows?: number;
  maxlength?: number;
  minlength?: number;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  autocomplete?: string;
  hasError?: boolean;
  richEditorConfig?: DSRichEditorConfig;
}

const props = defineProps<DSTextareaRichEditorProps>();

const modelValue = defineModel<DSTextareaModelValue>({ default: "" });

const emit = defineEmits<DSTextareaEmits>();

const textareaRef = ref<HTMLTextAreaElement>();
const editorRef = shallowRef<EasyEditorInstance | null>(null);

const { focus: focusTextarea, blur: blurTextarea } = useDSInput(textareaRef);

const normalizedModelValue = computed(() => modelValue.value ?? "");

const rootClasses = computed(() => ({
  [DS_TEXTAREA_CLASSES.Root]: true,
  [DS_STATE_CLASSES.Disabled]: props.disabled,
  [DS_STATE_CLASSES.Error]: props.hasError,
}));

const editorConfig = computed<DSRichEditorConfig>(() => {
  const config = { ...(props.richEditorConfig ?? {}) };

  if (!config.placeholder && props.placeholder) {
    config.placeholder = props.placeholder;
  }

  return config;
});

let isUpdatingFromEditor = false;
let isUpdatingFromModel = false;
let isFocusPending = false;
let initializing = false;
let hasFocus = false;
let lastFocusEvent: FocusEvent | undefined;
let lastBlurEvent: FocusEvent | undefined;
let deferredTextareaBlur: FocusEvent | undefined;

const isTrackedTarget = (target: EventTarget | null, tracker = editorRef.value?.ui.focusTracker): boolean => {
  if (!(target instanceof Node) || !tracker) return false;
  return (
    tracker.elements.some(element => element.contains(target)) ||
    tracker.externalViews.some(view => isTrackedTarget(target, view.focusTracker))
  );
};

const cancelPendingFocus = () => {
  isFocusPending = false;
  const event = deferredTextareaBlur;
  deferredTextareaBlur = undefined;
  if (event) onBlur(event);
};

useEventListener(
  document,
  "focus",
  event => {
    lastFocusEvent = event;
    if (deferredTextareaBlur && event.target !== textareaRef.value && !isTrackedTarget(event.target)) {
      cancelPendingFocus();
    }
  },
  true
);
useEventListener(
  document,
  "blur",
  event => {
    if (isTrackedTarget(event.target)) {
      lastBlurEvent = event;
    }
  },
  true
);

const onFocus = (event: FocusEvent) => {
  if (hasFocus) return;
  hasFocus = true;
  emit("focus", event);
};
const onBlur = (event: FocusEvent) => {
  if (!hasFocus) return;
  hasFocus = false;
  emit("blur", event);
};
const onTextareaFocus = (event: FocusEvent) => {
  if (editorRef.value) return;
  isFocusPending = true;
  onFocus(event);
};
const onTextareaBlur = (event: FocusEvent) => {
  if (editorRef.value) return;
  // CKEditor hides its source textarea before its asynchronous initialization resolves.
  if (isFocusPending && initializing && !event.relatedTarget && textareaRef.value?.style.display === "none") {
    deferredTextareaBlur = event;
    return;
  }
  isFocusPending = false;
  onBlur(event);
};

const getEasyEditor = (): EasyEditorApi => window.EasyEditor;

const bindEditorFocusEvents = () => {
  // eslint-disable-next-line max-params -- CKEditor supplies this four-argument focus-tracker callback.
  editorRef.value?.ui.focusTracker.on("change:isFocused", (_event, _name, isFocused, wasFocused) => {
    if (!wasFocused && isFocused) {
      onFocus(lastFocusEvent ?? new FocusEvent("focus"));
    }

    if (wasFocused && !isFocused) {
      onBlur(lastBlurEvent ?? new FocusEvent("blur"));
    }
  });
  // easyEditor-ready listeners may focus the editor before initEditor resolves, so no change event follows.
  if (editorRef.value?.ui.focusTracker.isFocused) onFocus(lastFocusEvent ?? new FocusEvent("focus"));
};

const syncReadOnlyState = () => {
  if (!editorRef.value) {
    return;
  }

  if (props.disabled || props.readonly) {
    editorRef.value.enableReadOnlyMode(EDITOR_READ_ONLY_LOCK);
  } else {
    editorRef.value.disableReadOnlyMode(EDITOR_READ_ONLY_LOCK);
  }
};

const syncModelFromEditor = () => {
  if (!editorRef.value || isUpdatingFromModel) {
    return;
  }

  const editorData = editorRef.value.getData();

  if (editorData === normalizedModelValue.value) {
    return;
  }

  isUpdatingFromEditor = true;
  modelValue.value = editorData;
  isUpdatingFromEditor = false;
};

const syncEditorFromModel = async (value: DSTextareaModelValue) => {
  if (!editorRef.value || isUpdatingFromEditor) {
    return;
  }

  const normalizedValue = value ?? "";

  if (editorRef.value.getData() === normalizedValue) {
    return;
  }

  isUpdatingFromModel = true;
  await Promise.resolve(editorRef.value.setData(normalizedValue));
  isUpdatingFromModel = false;
};

const focus = () => {
  if (props.disabled) return;
  if (editorRef.value) {
    editorRef.value.focus();
    return;
  }

  isFocusPending = true;
  focusTextarea();
};

const blur = () => {
  cancelPendingFocus();
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && isTrackedTarget(activeElement)) {
    activeElement.blur();
  }
  blurTextarea();
};

onMounted(async () => {
  await nextTick();
  window.EASY.schedule.require(initEditor, getEasyEditor);
});

const initEditor = async () => {
  initializing = true;
  const modelValueAtInitialization = normalizedModelValue.value;

  editorRef.value = markRaw(
    await getEasyEditor().initEditor({
      id: props.id,
      config: editorConfig.value,
    })
  );

  bindEditorFocusEvents();
  initializing = false;
  editorRef.value.model.document.on("change:data", syncModelFromEditor);

  syncReadOnlyState();
  if (normalizedModelValue.value === modelValueAtInitialization) {
    syncModelFromEditor();
  } else {
    await syncEditorFromModel(modelValue.value);
  }

  if (isFocusPending && !props.disabled) {
    isFocusPending = false;
    editorRef.value.focus();
    deferredTextareaBlur = undefined;
  } else {
    cancelPendingFocus();
  }
};

onBeforeUnmount(async () => {
  isFocusPending = false;
  deferredTextareaBlur = undefined;

  if (!editorRef.value) {
    return;
  }

  if (window.EasyEditor?.destroyEditor) {
    await window.EasyEditor.destroyEditor(props.id);
    return;
  }

  await editorRef.value.destroy();
});

watch(modelValue, syncEditorFromModel);
watch(() => props.disabled, syncReadOnlyState);
watch(() => props.readonly, syncReadOnlyState);

defineExpose({
  focus,
  blur,
});
</script>
